import { useState, useEffect, useRef } from "react";
import { productPath } from "../../../lib/productUrls";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Sparkles, Scissors, Plus, Check } from "lucide-react";
import { useCart } from "../../../auth/CartContext";
import { imgSrc } from "../pdp/completeTheLookMatch";
import { MAYA_PORTRAIT_URL } from "../../../lib/mayaPortrait";
import MayaCuratingIndicator from "../../../pages/shop/custom-studio/v4/components/MayaCuratingIndicator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "jns_ai_stylist_session";

const OPENERS = [
    "What's new this week?",
    "Daily wear cotton kurti under ₹1500",
    "I have a wedding to attend",
    "Can I return if size doesn't fit?",
    "Stitch me a pastel anarkali",
    "Difference between georgette and chiffon?",
];

/**
 * iter239 — AI Stylist drawer.
 *
 * A floating chat with 'Maya' — JnS's in-house personal shopper. Reads
 * intent ("I have a sangeet, budget 3-5K"), pulls live inventory, and
 * surfaces 1-4 outfits with a "why it fits" line per card.
 *
 * Designed to be the high-conversion alternative to drowning in a 50-item
 * grid. Customer types → 2 second wait → tap a card → straight to PDP.
 */
export default function AIStylistDrawer({ open, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
    });
    const [chips, setChips] = useState(OPENERS);
    const scrollRef = useRef(null);

    // Auto-scroll on new message
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, busy]);

    // Resume previous session
    useEffect(() => {
        if (!open || !sessionId || messages.length > 0) return;
        let cancelled = false;
        axios
            .get(`${API}/ai-stylist/session/${sessionId}`)
            .then((r) => {
                if (cancelled) return;
                const turns = r.data?.turns || [];
                const hydrated = [];
                for (const t of turns) {
                    if (t.user) hydrated.push({ role: "user", text: t.user });
                    if (t.assistant) hydrated.push({ role: "assistant", text: t.assistant, items: [] });
                }
                setMessages(hydrated);
            })
            .catch(() => {
                // Session expired or never existed — fine, start fresh
                try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
                setSessionId(null);
            });
        return () => { cancelled = true; };
    }, [open, sessionId, messages.length]);

    const send = async (text) => {
        const trimmed = (text || "").trim();
        if (!trimmed || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: trimmed }]);
        setBusy(true);
        try {
            const r = await axios.post(`${API}/ai-stylist/chat`, {
                session_id: sessionId,
                message: trimmed,
            });
            const data = r.data || {};
            if (data.session_id && data.session_id !== sessionId) {
                setSessionId(data.session_id);
                try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch { /* noop */ }
            }
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    text: data.reply || "",
                    items: data.item_cards || [],
                    custom_hint: !!data.custom_studio_hint,
                    custom_design: data.custom_design || null,
                    follow_up_question: data.follow_up_question || "",
                    view_all: data.view_all || null,  // iter253 — "Browse all X matching" CTA
                },
            ]);
            setChips(data.follow_up_chips?.length ? data.follow_up_chips : OPENERS);
        } catch (e) {
            setMessages((m) => [
                ...m,
                { role: "assistant", text: e?.response?.data?.detail || "Sorry, I'm having a tiny hiccup — please try again.", items: [] },
            ]);
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="ai-stylist-drawer"
            data-nosnippet
            className="fixed inset-0 z-[100] flex items-end sm:items-center sm:justify-end"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
            {/* Sheet */}
            <div
                onClick={(e) => e.stopPropagation()}
                /* iter257c — Mobile sheet now uses 88% of the visible
                   viewport (up from 80%) for a more immersive feel,
                   while three safety nets ensure the header never
                   crops on iOS Safari:
                     1. `dvh` (dynamic viewport) excludes the URL bar
                        and resizes as it shows/hides.
                     2. `min()` clamps height so we never exceed
                        100dvh minus the notch (env safe-area-inset-top)
                        minus 8px breathing room.
                     3. Mobile sheet keeps a small backdrop peek at
                        the top via `top` being inside `inset-0` flex
                        container with items-end. */
                style={{
                    height: "min(88dvh, calc(100dvh - env(safe-area-inset-top, 0px) - 8px))",
                }}
                className="relative z-10 w-full sm:max-w-md sm:!h-[88dvh] sm:!max-h-[88dvh] sm:mr-6 bg-ivory rounded-t-3xl sm:rounded-3xl border border-stone-200 shadow-2xl flex flex-col overflow-hidden pb-[env(safe-area-inset-bottom)]"
            >
                {/* Mobile grab handle — universal "bottom sheet" hint. Hidden on sm+. */}
                <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0" aria-hidden="true">
                    <span className="w-10 h-1 rounded-full bg-stone-300" />
                </div>
                {/* Header */}
                <div className="px-5 pt-5 pb-4 border-b border-stone-200 bg-gradient-to-br from-ivory-soft to-ivory">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                            {/* iter278.4 — Maya's portrait in the drawer header.
                                Same illustration as the in-studio consultant
                                so customers see ONE face across the whole site. */}
                            <span className="relative w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-md shrink-0">
                                <img
                                    src={MAYA_PORTRAIT_URL}
                                    alt="Maya"
                                    className="w-full h-full object-cover"
                                    loading="eager"
                                />
                                <span
                                    className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white"
                                    aria-label="Maya is online"
                                />
                            </span>
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold flex items-center gap-2 mb-1">
                                    <Sparkles size={11} />
                                    Maya · AI Stylist
                                </p>
                                <h3 className="font-display text-xl text-ink leading-tight">
                                    Hi! What are you{" "}
                                    <span className="italic text-wine">dressing for</span> today?
                                </h3>
                                <p className="text-[12px] text-ink-soft mt-1.5">
                                    Tell me the occasion, budget &amp; size — I&apos;ll curate.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="ai-stylist-close"
                            className="w-9 h-9 rounded-full hover:bg-stone-100 inline-flex items-center justify-center text-ink-soft shrink-0"
                            aria-label="Close stylist"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {messages.length === 0 && (
                        /* iter257 — empty state now centers vertically AND surfaces
                           the suggestion chips inline so the experience feels like
                           a curated welcome rather than a vast blank canvas. The
                           bottom chip strip is hidden in this state (see below). */
                        <div className="min-h-full flex flex-col items-center justify-center text-center py-4">
                            {/* iter278.4 — Maya's face in the empty state too,
                                so the very first impression is a person, not
                                a generic chat icon. */}
                            <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-md mb-3 ring-2 ring-rosegold/40">
                                <img
                                    src={MAYA_PORTRAIT_URL}
                                    alt="Maya"
                                    className="w-full h-full object-cover"
                                    loading="eager"
                                />
                            </div>
                            <p className="text-sm text-ink-soft max-w-[24ch] leading-relaxed mb-4">
                                Try something like&hellip;
                            </p>
                            <div className="grid grid-cols-1 gap-2 w-full max-w-[280px]">
                                {OPENERS.slice(0, 4).map((c, i) => {
                                    const isCustom = /custom/i.test(c) || /build/i.test(c) || /stitch/i.test(c);
                                    if (isCustom) {
                                        return (
                                            <Link
                                                to="/custom-studio"
                                                key={i}
                                                data-testid={`ai-stylist-opener-custom-${i}`}
                                                onClick={onClose}
                                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-rosegold text-ivory border border-rosegold text-[12px] font-bold uppercase tracking-[0.1em] hover:bg-wine transition"
                                            >
                                                <Scissors size={11} />
                                                {c}
                                            </Link>
                                        );
                                    }
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => send(c)}
                                            data-testid={`ai-stylist-opener-${i}`}
                                            className="px-4 py-2.5 rounded-full bg-white border border-stone-200 text-[12px] text-ink hover:border-rosegold hover:text-rosegold transition leading-snug"
                                        >
                                            {c}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {messages.map((m, i) => (
                        <MessageBubble key={i} message={m} onNavigate={onClose} />
                    ))}

                    {busy && (
                        <MayaCuratingIndicator
                            phrases={[
                                "Searching our racks for you…",
                                "Looking through the season's edit…",
                                "Pulling 2-3 strong picks…",
                                "Cross-checking with your budget…",
                                "Adding her boutique touch…",
                            ]}
                        />
                    )}
                </div>

                {/* Chips — only after Maya has replied at least once. The
                    empty state above already shows curated openers inline. */}
                {chips.length > 0 && !busy && messages.length > 0 && (
                    <div className="px-4 py-2 border-t border-stone-200 bg-ivory-soft">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                            {chips.map((c, i) => {
                                const isCustom = /custom/i.test(c) || /build/i.test(c);
                                if (isCustom) {
                                    return (
                                        <Link
                                            to="/custom-studio"
                                            key={i}
                                            data-testid={`ai-stylist-chip-custom-${i}`}
                                            onClick={onClose}
                                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rosegold text-ivory border border-rosegold text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-wine transition"
                                        >
                                            <Scissors size={11} />
                                            {c}
                                        </Link>
                                    );
                                }
                                return (
                                    <button
                                        key={i}
                                        onClick={() => send(c)}
                                        data-testid={`ai-stylist-chip-${i}`}
                                        className="shrink-0 px-3 py-1.5 rounded-full bg-white border border-stone-300 text-[11px] text-ink-soft hover:border-rosegold hover:text-rosegold transition"
                                    >
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Composer */}
                <form
                    className="px-4 py-3 border-t border-stone-200 bg-white"
                    onSubmit={(e) => { e.preventDefault(); send(input); }}
                >
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="e.g. Sangeet, ₹3-5K, size M"
                            disabled={busy}
                            data-testid="ai-stylist-input"
                            className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-[16px] focus:outline-none focus:ring-2 focus:ring-rosegold/40 focus:border-rosegold disabled:opacity-60"
                        />
                        <button
                            type="submit"
                            disabled={busy || !input.trim()}
                            data-testid="ai-stylist-send"
                            className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-ink text-ivory hover:bg-rosegold disabled:opacity-40 transition shrink-0"
                            aria-label="Send"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


// Minimal renderer: handles `**bold**` and Markdown-style links
// `[label](/path)`. Maya is instructed to use only these two
// constructs + \n\n for paragraph breaks. Bundle stays lean (no full
// markdown lib).
function renderRich(text, onLinkNav) {
    if (!text) return null;
    // Split on either **bold** OR [label](/path) tokens — keep delimiters.
    const tokenRe = /(\*\*[^*]+\*\*|\[[^\]]+\]\(\/[^)]+\))/g;
    const parts = String(text).split(tokenRe);
    return parts.map((p, i) => {
        if (!p) return null;
        if (p.startsWith("**") && p.endsWith("**")) {
            return <strong key={i} className="font-semibold text-ink">{p.slice(2, -2)}</strong>;
        }
        const linkMatch = /^\[([^\]]+)\]\((\/[^)]+)\)$/.exec(p);
        if (linkMatch) {
            const [, label, path] = linkMatch;
            return (
                <Link
                    key={i}
                    to={path}
                    data-testid={`ai-stylist-inline-link-${path.replace(/[^a-z0-9]/gi, "-")}`}
                    onClick={onLinkNav}
                    className="inline text-rosegold font-semibold underline decoration-rosegold/40 underline-offset-2 hover:decoration-rosegold transition"
                >
                    {label}
                </Link>
            );
        }
        return <span key={i}>{p}</span>;
    });
}


// An item can be added straight to the bag ONLY when no size choice is
// needed — jewellery / one-size / free-size pieces. Stylist cards from the
// backend reliably carry `department_slug`; `size_applicable` / `sizes` are
// defensive fallbacks in case the card shape ever widens. Sized clothing
// stays link-only so we never add the wrong size.
function isSizeless(item) {
    if (!item) return false;
    if (item.department_slug === "jewellery") return true;
    if (item.size_applicable === false) return true;
    if (Array.isArray(item.sizes) && item.sizes.length === 0) return true;
    return false;
}

function MessageBubble({ message, onNavigate }) {
    const { add } = useCart();
    const [added, setAdded] = useState({});   // id → true, brief tick state

    const handleAdd = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        // Stylist cards use `image_url`/`price` and carry NO stock count, so we
        // normalise to the shape the cart expects (photo_url + selling_price)
        // and pass availableForSize:1 — without it cart's `available` is
        // undefined and the line quantity becomes NaN. Maya only ever surfaces
        // in-stock pieces; the shopper can bump quantity in the bag.
        add(
            { ...item, photo_url: item.image_url, selling_price: item.price },
            1,
            { size: null, availableForSize: 1 },
        );
        toast.success(`${item.name} added to bag`);
        setAdded((prev) => ({ ...prev, [item.id]: true }));
        setTimeout(() => setAdded((prev) => ({ ...prev, [item.id]: false })), 1600);
    };

    if (message.role === "user") {
        return (
            <div className="flex justify-end">
                <div className="max-w-[80%] px-4 py-2.5 bg-wine text-ivory rounded-2xl rounded-tr-md text-sm leading-relaxed">
                    {message.text}
                </div>
            </div>
        );
    }
    return (
        <div className="flex justify-start">
            <div className="max-w-[88%]">
                <div className="px-4 py-2.5 bg-ivory-soft border border-stone-200 rounded-2xl rounded-tl-md text-sm leading-relaxed text-ink whitespace-pre-wrap">
                    {renderRich(message.text, onNavigate)}
                </div>
                {message.items && message.items.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2.5">
                        {message.items.map((it) => (
                            <Link
                                to={productPath(it)}
                                key={it.id}
                                onClick={onNavigate}
                                data-testid={`ai-stylist-item-${it.id}`}
                                className="group block rounded-2xl overflow-hidden border border-stone-200 bg-white hover:border-rosegold transition"
                            >
                                <div className="relative aspect-[3/4] bg-stone-100 overflow-hidden">
                                    <img
                                        src={imgSrc(it.image_url)}
                                        alt={it.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition"
                                        onError={(e) => { e.target.src = "/default-item.svg"; }}
                                    />
                                    {/* Inline add-to-bag — sizeless pieces only
                                        (jewellery / one-size). Sized clothing stays
                                        link-only so we never add the wrong size. */}
                                    {isSizeless(it) && (
                                        <button
                                            type="button"
                                            onClick={(e) => handleAdd(e, it)}
                                            data-testid={`ai-stylist-add-${it.id}`}
                                            aria-label={`Add ${it.name} to bag`}
                                            className={`absolute bottom-2 right-2 h-9 rounded-full text-ivory inline-flex items-center justify-center shadow-md backdrop-blur-sm transition-all duration-300 active:scale-95 ${
                                                added[it.id] ? "w-9 bg-emerald-600" : "w-9 hover:w-[104px] bg-ink/90 hover:bg-rosegold"
                                            }`}
                                        >
                                            {added[it.id] ? (
                                                <Check size={15} strokeWidth={2.5} />
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 whitespace-nowrap">
                                                    <Plus size={15} strokeWidth={2} className="flex-shrink-0" />
                                                    <span className="text-[10px] font-bold uppercase tracking-wider max-w-0 group-hover:max-w-[70px] opacity-0 group-hover:opacity-100 transition-all duration-300">Add to bag</span>
                                                </span>
                                            )}
                                        </button>
                                    )}
                                </div>
                                <div className="p-2.5">
                                    <p className="text-[11px] text-ink font-semibold leading-tight line-clamp-2 mb-1">
                                        {it.name}
                                    </p>
                                    <p className="text-[12px] text-rosegold font-bold">
                                        ₹{Number(it.price).toLocaleString("en-IN")}
                                    </p>
                                    <p className="text-[10px] text-ink-soft mt-1 leading-snug line-clamp-3 italic">
                                        {it.why_it_fits}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
                {/* iter253 — "Browse all matching" CTA. Shows when Maya
                    curated a few picks but more match the customer's
                    criteria. Deep-links to the relevant /c/<slug> page. */}
                {message.view_all && message.view_all.href && (
                    <Link
                        to={message.view_all.href}
                        onClick={onNavigate}
                        data-testid="ai-stylist-view-all"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-rosegold/40 bg-rosegold/5 hover:bg-rosegold/15 hover:border-rosegold text-rosegold text-[12px] font-bold tracking-wide transition group"
                    >
                        {message.view_all.label || `Browse all ${message.view_all.count} matching`}
                        <span className="transition group-hover:translate-x-0.5">→</span>
                    </Link>
                )}
                {message.custom_design && (
                    <Link
                        to={message.custom_design.template_slug ? `/custom-studio/visual?template=${message.custom_design.template_slug}&fresh=1` : "/custom-studio"}
                        onClick={onNavigate}
                        data-testid="ai-stylist-custom-design-card"
                        className="mt-3 group block rounded-2xl overflow-hidden border border-rosegold/50 bg-gradient-to-br from-rosegold/15 via-rosegold/5 to-transparent hover:border-rosegold transition"
                    >
                        <div className="p-3.5">
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rosegold text-ivory shrink-0">
                                    <Scissors size={13} />
                                </span>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-rosegold font-bold">
                                    Custom design Maya picked
                                </p>
                            </div>
                            {message.custom_design.template_name && (
                                <p className="text-[14px] font-bold text-ink leading-tight">
                                    {message.custom_design.template_name}
                                    {message.custom_design.fabric_summary && (
                                        <span className="font-normal text-ink-soft"> · {message.custom_design.fabric_summary}</span>
                                    )}
                                </p>
                            )}
                            {message.custom_design.design_notes && (
                                <p className="text-[11px] text-ink-soft mt-1.5 leading-snug italic line-clamp-3">
                                    {message.custom_design.design_notes}
                                </p>
                            )}
                            <div className="mt-2.5 flex items-center justify-between gap-2 text-[11px]">
                                <div className="flex items-center gap-2">
                                    {message.custom_design.estimated_base_inr ? (
                                        <span className="font-bold text-rosegold">
                                            ~₹{Number(message.custom_design.estimated_base_inr).toLocaleString("en-IN")}
                                        </span>
                                    ) : null}
                                    {message.custom_design.est_days_min ? (
                                        <span className="text-ink-soft">
                                            · {message.custom_design.est_days_min}–{message.custom_design.est_days_max} days
                                        </span>
                                    ) : null}
                                </div>
                                <span className="inline-flex items-center gap-1 text-rosegold font-bold uppercase tracking-[0.12em]">
                                    Start designing <Sparkles size={11} />
                                </span>
                            </div>
                        </div>
                    </Link>
                )}
                {message.follow_up_question && (
                    <p className="mt-2.5 px-3 py-2 rounded-xl bg-ivory border border-rosegold/20 text-[12px] text-ink-soft italic" data-testid="ai-stylist-follow-up-question">
                        {message.follow_up_question}
                    </p>
                )}
                {message.custom_hint && !message.custom_design && (
                    <Link
                        to="/custom-studio"
                        onClick={onNavigate}
                        data-testid="ai-stylist-custom-cta"
                        className="mt-3 group block rounded-2xl overflow-hidden border border-rosegold/40 bg-gradient-to-br from-rosegold/15 via-rosegold/5 to-transparent hover:border-rosegold transition"
                    >
                        <div className="flex items-center gap-3 p-3">
                            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-rosegold text-ivory shrink-0">
                                <Scissors size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-bold text-ink leading-tight">
                                    Don&apos;t see your dream piece?
                                </p>
                                <p className="text-[11px] text-ink-soft mt-0.5 leading-snug">
                                    Build it custom — your fabric, your design, your measurements.
                                </p>
                            </div>
                            <Sparkles size={14} className="text-rosegold shrink-0 group-hover:rotate-12 transition" />
                        </div>
                    </Link>
                )}
            </div>
        </div>
    );
}
